import { Icon, IconProps } from "@/components/ui/Icon";
import { IconName } from "@/components/ui/Icon/names";
import React, { useMemo } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { ReleaseStage } from "./components/release-stage-badge";
import { useSlugs } from "./contexts/Sdk";
import { cn } from "./lib/utils";
import AssistantPage from "./pages/assistants/Assistant";
import AssistantsIndex, { AssistantsRoot } from "./pages/assistants/Assistants";
import NewAssistantPage from "./pages/assistants/NewAssistant";
import Billing from "./pages/billing/Billing";
import Catalog, { CatalogRoot } from "./pages/catalog/Catalog";
import CatalogDetail, {
  CatalogDetailRoot,
} from "./pages/catalog/CatalogDetail";
import ChatSessions from "./pages/chatLogs/ChatLogs";
import OrgMemory from "./components/observe/OrgMemory";
import { ChatConversation, ChatHome, ChatRoot } from "./pages/chat/Chat";
import Skills from "./pages/Skills";
import SkillsList from "./pages/skills/SkillsList";
import SkillContent from "./pages/skills/SkillContent";
import SkillDetailRoot from "./pages/skills/SkillDetailRoot";
import SkillFeedback from "./pages/skills/SkillFeedback";
import SkillOverview from "./pages/skills/SkillOverview";
import SkillScoredSessions from "./pages/skills/SkillScoredSessions";
import SkillSettings from "./pages/skills/SkillSettings";
import SkillUsage from "./pages/skills/SkillUsage";
import SkillVersionHistory from "./pages/skills/SkillVersionHistory";
import Deployment from "./pages/deployments/deployment/Deployment";
import Deployments, { DeploymentsRoot } from "./pages/deployments/Deployments";
import ProjectIdentityRedirect from "./pages/org/ProjectIdentityRedirect";
import UserSessions from "./pages/org/UserSessions";
import EventFeed from "./pages/data/EventFeed";
import DataExports from "./pages/data-exports/DataExports";
import { LegacyDataRedirect } from "./pages/data-exports/LegacyDataRedirect";
import DeviceAgent, { DeviceAgentRoot } from "./pages/device-agent/DeviceAgent";
import AgentsPage from "./pages/agents/Agents";
import MdmIntegrationDetail from "./pages/org/device-integrations/MdmIntegrationDetail";
import EnvironmentPage from "./pages/environments/Environment";
import Environments, {
  EnvironmentsRoot,
} from "./pages/environments/Environments";
import Home from "./pages/home/Home";
import { ProjectGuidePage } from "./components/project-guide/ProjectGuidePage";
import Integrations from "./pages/integrations/Integrations";
import Login from "./pages/login/Login";
import ExploreDemo from "./pages/demo/ExploreDemo";
import SignUp from "./pages/login/SignUp";
import { LogsRoot } from "./pages/logs/Logs";
import { MCPDetailPage } from "./pages/mcp/MCPDetails";
import { MCPPage, MCPRoot } from "./pages/mcp/MCP";
import AddMcpServer, { AddMcpServerRoot } from "./pages/mcp/add/AddMcpServer";
import CreateFromSource from "./pages/mcp/add/CreateFromSource";
import { RedirectToDeployments } from "./pages/mcp/add/LegacyRedirects";
import Sources, { SourcesRoot } from "./pages/mcp/sources/Sources";
import SourceDetailRoute from "./pages/mcp/sources/SourceDetail";
import GatewayDetailPage from "./pages/mcp/gateway/GatewayDetails";
import MCPServerDetails from "./pages/mcp/x/MCPServerDetails";
import { InsightsHooksPage, InsightsRoot } from "./pages/insights/Insights";
import Costs from "./pages/costs/Costs";
import {
  BuiltInDashboardRoute,
  DashboardRoute,
  DashboardsIndex,
  DashboardsRoot,
} from "./pages/explore/Dashboards";
import Explore from "./pages/explore/Explore";
import SignalsIntelligenceRoute from "./components/signals-intelligence-route";
import IdentitiesIndex, {
  IdentityDetailIndexRedirect,
  IdentitiesRoot,
} from "./pages/identities/IdentitiesIndex";
import IdentityDetailRoot from "./pages/identities/IdentityDetailRoot";
import IdentityOverview from "./pages/identities/IdentityOverview";
import IdentityAccess from "./pages/identities/IdentityAccess";
import IdentityUsage from "./pages/identities/IdentityUsage";
import IdentitySecurity from "./pages/identities/IdentitySecurity";
import IdentityFindingsPage from "./pages/identities/IdentityFindings";
import IdentityCost from "./pages/identities/IdentityCost";
import IdentityDevices from "./pages/identities/IdentityDevices";
import IdentityConnections from "./pages/identities/IdentityConnections";
import IdentityActivity from "./pages/identities/IdentityActivity";
import FunctionsOnboarding from "./pages/onboarding/FunctionsOnboarding";
import UploadOpenAPI from "./pages/onboarding/UploadOpenAPI";
import CreateRemoteMcp from "./pages/sources/remote-mcp/CreateRemoteMcp";
import CreateTunneledMcp from "./pages/sources/tunneled-mcp/CreateTunneledMcp";
import OrgApiKeys from "./pages/org/OrgApiKeys";
import Plugins, { PluginsRoot } from "./pages/plugins/Plugins";
import PluginDetail from "./pages/plugins/PluginDetail";
import OrgAuditLogs from "./pages/org/OrgAuditLogs";
import OrgDomains from "./pages/org/OrgDomains";
import OrgHome from "./pages/org/OrgHome";
import OrgIdentity from "./pages/org/OrgIdentity";
import OrgAIIntegrations from "./pages/org/OrgAIIntegrations";
import OrgLogs from "./pages/org/OrgLogs";
import OrgGateway from "./pages/org/OrgGateway";
import HeadlessMode from "./pages/org/HeadlessMode";
import PlatformMCPRedirect from "./pages/org/PlatformMCPRedirect";
import OrgSkills from "./pages/org/OrgSkills";
import ExternalCredentialDetail from "./pages/org/external-services/ExternalCredentialDetail";
import {
  ExternalServicesPage,
  ExternalServicesRoot,
} from "./pages/org/external-services/ExternalServices";
import ExternalKeyDetail from "./pages/org/encryption-keys/ExternalKeyDetail";
import JsonWebKeySetDetail, {
  SigningKeySetsIndex,
  SigningKeySetsRoot,
} from "./pages/org/encryption-keys/jwks/JsonWebKeySetDetail";
import {
  EncryptionKeysPage,
  EncryptionKeysRoot,
} from "./pages/org/encryption-keys/EncryptionKeys";
import OrgWebhooks from "./pages/org/OrgWebhooks";
import UserSessionIssuerDetail from "./pages/user-session-issuers/UserSessionIssuerDetail";
import {
  UserSessionIssuersPage,
  UserSessionIssuersRoot,
} from "./pages/user-session-issuers/UserSessionIssuers";
import {
  RemoteIdentityProvidersPage,
  RemoteIdentityProvidersRoot,
} from "./pages/remote-identity-providers/RemoteIdentityProviders";
import {
  WorkloadIssuersPage,
  WorkloadIssuersRoot,
} from "./pages/workload-identities/WorkloadIssuers";
import { WorkloadIssuerDetailPage } from "./pages/workload-identities/WorkloadIssuerDetail";
import { CatalogPlatformPage } from "./pages/workload-identities/setup/CatalogPlatformPage";
import AccessHubRedirect from "./pages/workload-identities/AccessHubRedirect";
import RemoteIdentityProviderDetail from "./pages/remote-identity-providers/RemoteIdentityProviderDetail";
import RemoteSessionClientDetail from "./pages/remote-identity-providers/RemoteSessionClientDetail";
import PlatformAdminOverview from "./pages/platform-admin/Overview";
import PlatformAdminRbacOverride from "./pages/platform-admin/RbacOverride";
import PlatformAdminOnboarding from "./pages/platform-admin/Onboarding";
import PlatformAdminOpenRouterKeys from "./pages/platform-admin/OpenRouterKeys";
import Playground from "./pages/playground/Playground";
import NewPromptPage from "./pages/prompts/NewPrompt";
import PromptPage from "./pages/prompts/Prompt";
import Prompts, { PromptsRoot } from "./pages/prompts/Prompts";
import SDK from "./pages/sdk/SDK";
import Access from "./pages/access/Access";
import { RoleEditorPage } from "./pages/access/RoleEditorPage";
import RequestAccess from "./pages/access/RequestAccess";
import Settings from "./pages/settings/Settings";
import TriggersIndex, { TriggersRoot } from "./pages/triggers/Triggers";
import SecurityOverview, {
  RiskOverviewRoot,
} from "./pages/security/SecurityOverview";
import Watchdog from "./pages/security/watchdog/Watchdog";
import RiskEventsPage from "./pages/security/RiskEventsPage";
import {
  ShadowAIRoot,
  ShadowAISectionRedirect,
  ShadowMCPIndexRedirect,
  ShadowMCPServerLegacyRedirect,
} from "./pages/shadow-ai/ShadowAI";
import ShadowAIToolDetail from "./pages/shadow-ai/ShadowAIToolDetail";
import ShadowMCPServerDetail from "./pages/shadow-mcp/ShadowMCPServerDetail";
import RiskOverviewCategoriesIndex from "./pages/security/RiskOverviewCategoriesIndex";
import RiskOverviewCategoryDetail from "./pages/security/RiskOverviewCategoryDetail";
import RiskOverviewRulesIndex from "./pages/security/RiskOverviewRulesIndex";
import RiskOverviewUserDetail from "./pages/security/RiskOverviewUserDetail";
import RiskOverviewUsersIndex from "./pages/security/RiskOverviewUsersIndex";
import PolicyCenter, { PolicyCenterRoot } from "./pages/security/PolicyCenter";
import PolicyDetail, { PolicyNew } from "./pages/security/PolicyDetail";
import DetectionRules from "./pages/security/DetectionRules";
import Team from "./pages/team/Team";
import {
  KillswitchesRoot,
  KillswitchIndexRedirect,
  KillswitchRecordRedirect,
} from "./pages/killswitch/KillswitchesRoot";
import CustomTools, { CustomToolsRoot } from "./pages/toolBuilder/CustomTools";
import {
  ToolBuilderNew,
  ToolBuilderPage,
} from "./pages/toolBuilder/ToolBuilder";

const SetupWizard = React.lazy(() => import("./pages/setup/SetupWizard"));
const SetupTaskRedirect = React.lazy(
  () => import("./pages/setup/SetupTaskRedirect"),
);

type AppRouteBasic = {
  title: string;
  url: string;
  external?: boolean;
  icon?: IconName;
  customIcon?: React.ComponentType<{ className?: string }>;
  component?: React.ComponentType;
  indexComponent?: React.ComponentType;
  subPages?: AppRoutesBasic;
  unauthenticated?: boolean;
  outsideMainLayout?: boolean;
  // This route only exists to resolve an old URL: its component redirects
  // elsewhere. Surfaces that enumerate routes as destinations (the command
  // palette) skip it, so a bookmark keeps working without the dead entry
  // being offered as somewhere to go.
  legacyRedirect?: boolean;
  // Release stage badge shown on this route's nav entry. Use sparingly —
  // only for features that are genuinely pre-GA. Page-level badges live on
  // <Page.Section.Title stage="..." /> and must be set separately.
  stage?: ReleaseStage;
};

type GoToFunction = (...params: string[]) => void;

export type AppRoutes = Record<string, AppRoute>;
type AppRoutesBasic = Record<string, AppRouteBasic>;

// App route augmented with some additional utilities
export type AppRoute = Omit<AppRouteBasic, "icon" | "subPages"> & {
  Icon: React.ComponentType<Omit<IconProps, "name">>;
  active: boolean;
  // subPages?: AppRoutes;
  href: (...params: string[]) => string;
  goTo: GoToFunction;
  Link: React.ComponentType<{
    params?: string[];
    queryParams?: Record<string, string>;
    hash?: string;
    className?: string;
    children: React.ReactNode;
  }>;
};

type RouteEntry = {
  title: string;
  url: string;
  icon?: IconName;
  customIcon?: React.ComponentType<{ className?: string }>;
  stage?: ReleaseStage;
} & (
  | {
      external: true;

      component?: never;
      indexComponent?: never;
      unauthenticated?: never;
      subPages?: never;
    }
  | {
      external?: false;

      component?: React.ComponentType;
      indexComponent?: React.ComponentType;
      unauthenticated?: boolean;
      subPages?: Record<string, RouteEntry>;
      outsideMainLayout?: boolean;
      legacyRedirect?: boolean;
    }
);

const ROUTE_STRUCTURE = {
  login: {
    title: "Login",
    url: "/login",
    component: Login,
    unauthenticated: true,
  },
  register: {
    title: "Register",
    url: "/register",
    component: () => <Navigate to="/sign-up" replace />,
    unauthenticated: true,
  },
  exploreDemo: {
    title: "Explore demo",
    url: "/explore-demo",
    component: ExploreDemo,
    unauthenticated: true,
  },
  signUp: {
    title: "Sign up",
    url: "/sign-up",
    component: SignUp,
    unauthenticated: true,
  },
  home: {
    // "Home" now belongs to the org-level nav entry; the project's landing
    // page is its overview.
    title: "Project Overview",
    url: "",
    icon: "house",
    component: Home,
  },
  guide: {
    title: "Project Guide",
    url: "guide",
    component: ProjectGuidePage,
  },
  chat: {
    title: "Project Assistant",
    url: "chat",
    icon: "message-circle",
    stage: "beta",
    // Layout route: renders the index (ChatHome) or a conversation subpage.
    component: ChatRoot,
    indexComponent: ChatHome,
    subPages: {
      conversation: {
        title: "Chat",
        url: ":chatId",
        component: ChatConversation,
      },
    },
  },
  playground: {
    title: "Playground",
    url: "playground",
    icon: "message-circle",
    component: Playground,
  },
  integrations: {
    title: "Integrations",
    url: "integrations",
    icon: "package",
    component: Integrations,
  },
  customTools: {
    title: "Custom Tools",
    url: "custom-tools",
    icon: "pencil-ruler",
    component: CustomToolsRoot,
    indexComponent: CustomTools,
    subPages: {
      toolBuilderNew: {
        title: "Tool Builder",
        url: "new",
        component: ToolBuilderNew,
      },
      toolBuilder: {
        title: "Tool Builder",
        url: ":toolName",
        component: ToolBuilderPage,
      },
    },
  },
  prompts: {
    title: "Prompts",
    url: "prompts",
    icon: "newspaper",
    component: PromptsRoot,
    indexComponent: Prompts,
    subPages: {
      newPrompt: {
        title: "New Prompt",
        url: "new",
        component: NewPromptPage,
      },
      prompt: {
        title: "Edit Prompt",
        url: ":promptName",
        component: PromptPage,
      },
    },
  },
  assistants: {
    title: "Assistants",
    url: "assistants",
    icon: "bot",
    stage: "beta",
    component: AssistantsRoot,
    indexComponent: AssistantsIndex,
    subPages: {
      newAssistant: {
        title: "New Assistant",
        url: "new",
        component: NewAssistantPage,
      },
      detail: {
        title: "Assistant",
        url: ":assistantId",
        component: AssistantPage,
      },
    },
  },
  signalsIntelligence: {
    title: "Signals intelligence",
    url: "signals-intelligence",
    icon: "radio",
    stage: "preview",
    component: SignalsIntelligenceRoute,
  },
  skills: {
    title: "Skills",
    url: "skills",
    icon: "terminal",
    component: Skills,
    indexComponent: SkillsList,
    subPages: {
      detail: {
        title: "Skill",
        url: ":skillId",
        component: SkillDetailRoot,
        subPages: {
          overview: {
            title: "Skill Overview",
            url: "overview",
            component: SkillOverview,
          },
          content: {
            title: "Skill Content",
            url: "content",
            component: SkillContent,
          },
          usage: {
            title: "Skill Usage",
            url: "usage",
            component: SkillUsage,
          },
          scoredSessions: {
            title: "Scored Sessions",
            url: "scored-sessions",
            component: SkillScoredSessions,
          },
          feedback: {
            title: "Agent Feedback",
            url: "feedback",
            component: SkillFeedback,
          },
          versions: {
            title: "Skill Version History",
            url: "versions",
            component: SkillVersionHistory,
            subPages: {
              version: {
                title: "Skill Version",
                url: ":versionId",
                component: SkillVersionHistory,
              },
            },
          },
          settings: {
            title: "Settings",
            url: "settings",
            component: SkillSettings,
          },
        },
      },
    },
  },
  mcp: {
    title: "MCP",
    url: "mcp",
    icon: "network",
    component: MCPRoot,
    indexComponent: MCPPage,
    subPages: {
      // S-853: MCP is the inventory, so every way of adding a server starts
      // here. Options are named for how the server is reached rather than for
      // the backend that stores it, and OpenAPI and functions sit under
      // "advanced" on the same page instead of in the navigation.
      //
      // `add`, `catalog` and `sources` are static segments under `mcp`, so they
      // rank above the `details` `:toolsetSlug` route and are not swallowed by
      // it. The cost is that a toolset slugged "add", "catalog" or "sources"
      // becomes unreachable in the dashboard. Nothing reserves those slugs
      // server-side, so this is the same latent collision the sibling `x` and
      // `gateway` segments already carry.
      add: {
        title: "Add MCP Server",
        url: "add",
        component: AddMcpServerRoot,
        indexComponent: AddMcpServer,
        subPages: {
          remote: {
            title: "Add Remote MCP Server",
            url: "remote",
            component: CreateRemoteMcp,
          },
          tunneled: {
            title: "Add Tunneled MCP Server",
            url: "tunneled",
            component: CreateTunneledMcp,
          },
          fromSource: {
            title: "From Existing Source",
            url: "from-existing-source",
            component: CreateFromSource,
          },
          openapi: {
            title: "Add OpenAPI",
            url: "openapi",
            component: UploadOpenAPI,
          },
          function: {
            title: "Add Function",
            url: "function",
            component: FunctionsOnboarding,
          },
        },
      },
      // The single catalog entry point, and a tab of the MCP index rather than
      // a step inside the add flow: it is the one surface people go looking for
      // by name. `/catalog`, `/sources/add-from-catalog` and the earlier
      // `/mcp/add/catalog` all redirect here.
      catalog: {
        title: "Catalog",
        url: "catalog",
        component: CatalogRoot,
        indexComponent: Catalog,
        subPages: {
          detail: {
            title: "Server Details",
            url: ":serverSpecifier",
            component: CatalogDetailRoot,
            indexComponent: CatalogDetail,
          },
        },
      },
      // Sources have no section of their own any more, but the CLI links here
      // after a push and functions still arrive this way, so they keep a page.
      deployments: {
        title: "Deployments",
        url: "deployments",
        component: Deployments,
      },
      sources: {
        title: "Sources",
        url: "sources",
        component: SourcesRoot,
        indexComponent: Sources,
        subPages: {
          detail: {
            title: "Source",
            url: ":sourceId",
            component: SourceDetailRoute,
          },
        },
      },
      // TODO(AGE-1902): collapse with :toolsetSlug once Hosted (toolset-backed)
      // MCP data moves to mcp_servers/mcp_endpoints. Until then this route is
      // distinct so the new mcp_servers-backed details page renders against
      // mcp_servers without disturbing the existing toolset-backed path. The
      // `x/` prefix is the dashboard's generic experimental namespace; the
      // runtime path for these servers is `/mcp/{slug}` (see AGE-2555).
      x: {
        title: "MCP Server Details",
        url: "x/:mcpServerSlug",
        component: MCPServerDetails,
        subPages: {
          overview: {
            title: "MCP Server Overview",
            url: "overview",
          },
          inspect: {
            title: "MCP Server Inspect",
            url: "inspect",
          },
          // Legacy routes. MCPServerDetails redirects `authentication` to
          // settings#authentication now that authentication lives under
          // Settings, and `tools` to `inspect`.
          authentication: {
            title: "MCP Server Authentication",
            url: "authentication",
          },
          tools: {
            title: "MCP Server Tools",
            url: "tools",
          },
          teamAccess: {
            title: "MCP Server Team Access",
            url: "team-access",
          },
          guardrails: {
            title: "MCP Server Guardrails",
            url: "guardrails",
          },
          sessions: {
            title: "MCP Server Clients and Sessions",
            url: "sessions",
          },
          settings: {
            title: "MCP Server Settings",
            url: "settings",
          },
        },
      },
      // Gateway Endpoints (meta MCP servers). Addressed by id: a gateway has
      // no slug of its own — routing identity lives on its mcp_endpoints.
      gateway: {
        title: "Gateway Details",
        url: "gateway/:gatewayId",
        component: GatewayDetailPage,
        subPages: {
          overview: {
            title: "Gateway Overview",
            url: "overview",
          },
          // Legacy URL: member management moved onto the Overview tab, and
          // GatewayDetails redirects unknown tab segments there. Kept so old
          // links keep resolving to the gateway page.
          members: {
            title: "Gateway Members",
            url: "members",
          },
          inspect: {
            title: "Gateway Inspect",
            url: "inspect",
          },
          teamAccess: {
            title: "Gateway Team Access",
            url: "team-access",
          },
          sessions: {
            title: "Gateway Clients and Sessions",
            url: "sessions",
          },
          settings: {
            title: "Gateway Settings",
            url: "settings",
          },
        },
      },
      details: {
        title: "MCP Details",
        url: ":toolsetSlug",
        component: MCPDetailPage,
        subPages: {
          overview: {
            title: "MCP Overview",
            url: "overview",
          },
          tools: {
            title: "MCP Tools",
            url: "tools",
          },
          resources: {
            title: "MCP Resources",
            url: "resources",
          },
          prompts: {
            title: "MCP Prompts",
            url: "prompts",
          },
          authentication: {
            title: "MCP Authentication",
            url: "authentication",
          },
          performance: {
            title: "MCP Performance",
            url: "performance",
          },
          teamAccess: {
            title: "MCP Team Access",
            url: "team-access",
          },
          sessions: {
            title: "MCP Clients and Sessions",
            url: "sessions",
          },
          settings: {
            title: "MCP Settings",
            url: "settings",
          },
        },
      },
    },
  },
  environments: {
    title: "Environments",
    url: "environments",
    icon: "blocks",
    component: EnvironmentsRoot,
    indexComponent: Environments,
    subPages: {
      environment: {
        title: "Environment Details",
        url: ":environmentSlug",
        component: EnvironmentPage,
      },
    },
  },
  triggers: {
    title: "Triggers",
    url: "triggers",
    icon: "zap",
    component: TriggersRoot,
    indexComponent: TriggersIndex,
  },
  insights: {
    title: "MCP & Tools",
    url: "insights",
    icon: "layout-dashboard",
    component: InsightsRoot,
    indexComponent: InsightsHooksPage,
  },
  mcpSessions: {
    title: "MCP Sessions",
    url: "mcp-sessions",
    icon: "users",
    component: UserSessions,
  },

  remoteIdentityProviders: {
    title: "Remote Identity Providers",
    url: "remote-identity-providers",
    icon: "key-round",
    component: RemoteIdentityProvidersRoot,
    indexComponent: RemoteIdentityProvidersPage,
    subPages: {
      issuerDetail: {
        title: "Remote Identity Provider",
        url: ":issuerId",
        component: RemoteIdentityProviderDetail,
        subPages: {
          overview: { title: "Overview", url: "overview" },
          clients: { title: "Clients", url: "clients" },
          settings: { title: "Settings", url: "settings" },
        },
      },
      clientDetail: {
        title: "Remote Session Client",
        url: ":issuerId/clients/:clientId",
        component: RemoteSessionClientDetail,
        subPages: {
          overview: { title: "Overview", url: "overview" },
          mcpServers: { title: "MCP Servers", url: "mcp-servers" },
          sessions: { title: "Sessions", url: "sessions" },
          // Merged into Overview; RemoteSessionClientDetail redirects it there.
          settings: {
            title: "Settings",
            url: "settings",
            legacyRedirect: true,
          },
        },
      },
    },
  },

  // Legacy project-scoped URLs for the Access Hub and the Workload Identities
  // page redirect to the organization-level Access Hub.
  legacyWorkloadIdentities: {
    title: "Workload Identities",
    url: "workload-identities",
    legacyRedirect: true,
    component: AccessHubRedirect,
  },
  legacyAccessHub: {
    title: "Access Hub",
    url: "access-hub/*",
    legacyRedirect: true,
    component: AccessHubRedirect,
  },

  agents: {
    title: "Agent Identity",
    url: "agent-management",
    icon: "bot",
    stage: "preview",
    component: AgentsPage,
  },
  // One page per person, reached from every surface that renders a human. The
  // URL segment is an identity URN (`user:...`, `email:...`, `external:...`),
  // url-encoded; the resolver folds every identifier for a subject onto the
  // same canonical URN, so links built from different systems converge here.
  //
  // Project-level, where Employee Enrollment sat: a project is how an
  // organization segments the people it manages, and usage, cost, chats and
  // risk are all recorded per project. The org-scoped reads behind the page —
  // identity resolution, the directory, roles, devices, the audit trail and the
  // challenge log — answer the same whichever project you arrive from.
  identities: {
    title: "Identities",
    url: "identities",
    icon: "users",
    component: IdentitiesRoot,
    indexComponent: IdentitiesIndex,
    subPages: {
      detail: {
        title: "Identity",
        url: ":identityUrn",
        component: IdentityDetailRoot,
        indexComponent: IdentityDetailIndexRedirect,
        subPages: {
          overview: {
            title: "Identity Overview",
            url: "overview",
            component: IdentityOverview,
          },
          access: {
            title: "Identity Access",
            url: "access",
            component: IdentityAccess,
          },
          usage: {
            title: "Identity Usage",
            url: "usage",
            component: IdentityUsage,
          },
          security: {
            title: "Identity Security",
            url: "security",
            component: IdentitySecurity,
          },
          findings: {
            title: "Identity Findings",
            url: "findings",
            component: IdentityFindingsPage,
          },
          cost: {
            title: "Identity Cost",
            url: "cost",
            component: IdentityCost,
          },
          connections: {
            title: "Identity Connections",
            url: "connections",
            component: IdentityConnections,
          },
          devices: {
            title: "Identity Devices",
            url: "devices",
            component: IdentityDevices,
          },
          activity: {
            title: "Identity Activity",
            url: "activity",
            component: IdentityActivity,
          },
        },
      },
    },
  },
  costs: {
    title: "Costs",
    url: "costs",
    icon: "credit-card",
    component: Costs,
    subPages: {
      // Catch-all so the drill path (`/costs/Division~R&D/Department~Eng/…`)
      // renders the same explorer at any depth. CostsExplorer reads the drill
      // levels from the pathname; no per-depth route definition is needed.
      drill: {
        title: "Costs",
        url: "*",
        component: Costs,
      },
    },
  },
  explore: {
    title: "Explore",
    url: "explore",
    icon: "telescope",
    component: Explore,
    stage: "preview",
  },
  dashboards: {
    title: "Dashboards",
    url: "dashboards",
    icon: "layout-grid",
    component: DashboardsRoot,
    indexComponent: DashboardsIndex,
    stage: "preview",
    subPages: {
      builtIn: {
        title: "Dashboard",
        url: "builtin/:slug",
        component: BuiltInDashboardRoute,
      },
      detail: {
        title: "Dashboard",
        url: ":dashboardId",
        component: DashboardRoute,
      },
    },
  },
  logs: {
    title: "Tool Logs",
    url: "logs",
    icon: "logs",
    component: LogsRoot,
  },
  agentSessions: {
    title: "Agent Sessions",
    url: "agent-sessions",
    icon: "message-square",
    component: ChatSessions,
  },
  orgMemory: {
    title: "Org Memory",
    url: "org-memory",
    icon: "brain",
    stage: "preview",
    component: OrgMemory,
  },
  watchdog: {
    title: "Watchdog",
    url: "watchdog",
    icon: "radar",
    component: Watchdog,
  },
  riskOverview: {
    title: "Risk Overview",
    url: "risk-overview",
    icon: "shield",
    component: RiskOverviewRoot,
    indexComponent: SecurityOverview,
    subPages: {
      usersIndex: {
        title: "Users",
        url: "users",
        component: RiskOverviewUsersIndex,
      },
      userDetail: {
        title: "User",
        url: "users/:externalUserId",
        component: RiskOverviewUserDetail,
      },
      categoriesIndex: {
        title: "Categories",
        url: "categories",
        component: RiskOverviewCategoriesIndex,
      },
      rulesIndex: {
        title: "Rules",
        url: "rules",
        component: RiskOverviewRulesIndex,
      },
      categoryDetail: {
        title: "Category",
        url: "categories/:category",
        component: RiskOverviewCategoryDetail,
      },
    },
  },
  // Legacy URL: Detection Rules lives as a Guardrails tab now; the
  // component redirects there (carrying ?rule= deep links along). Kept out of
  // the sidebar.
  detectionRules: {
    title: "Detection Rules",
    url: "detection-rules",
    icon: "scan-search",
    component: DetectionRules,
  },
  policyCenter: {
    title: "Guardrails",
    url: "risk-policies",
    icon: "shield-check",
    // Layout route: renders the policy list (index) or a policy detail subpage.
    component: PolicyCenterRoot,
    indexComponent: PolicyCenter,
    subPages: {
      new: {
        title: "New policy",
        url: "new",
        component: PolicyNew,
      },
      detail: {
        title: "Policy",
        url: ":policyId",
        component: PolicyDetail,
      },
    },
  },
  riskEvents: {
    title: "Risk Events",
    url: "risk-events",
    icon: "flag",
    component: RiskEventsPage,
  },
  // Shadow AI lives in Guardrails (the Shadow AI policy and its targets tab)
  // and is not in the nav. Its section URLs stay because tickets, bookmarks
  // and block messages link to them: every index page redirects to
  // Guardrails, and the tool and server detail pages beneath keep rendering.
  //
  // Project-level placement with organization-scoped reads behind it, the
  // same shape as identities above and for the same reason.
  shadowAI: {
    title: "Shadow AI",
    url: "shadow-ai",
    icon: "shield",
    component: ShadowAIRoot,
    indexComponent: ShadowAISectionRedirect,
    subPages: {
      // A tool's detail is nested under its category, as the server detail
      // is under MCPs, so the breadcrumb says which kind a pasted link is.
      harnesses: {
        title: "Harnesses",
        url: "harnesses",
        component: ShadowAIRoot,
        indexComponent: ShadowAISectionRedirect,
        subPages: {
          detail: {
            title: "Shadow AI Tool",
            url: ":targetId",
            component: ShadowAIToolDetail,
          },
        },
      },
      assistants: {
        title: "Assistants",
        url: "assistants",
        component: ShadowAIRoot,
        indexComponent: ShadowAISectionRedirect,
        subPages: {
          detail: {
            title: "Shadow AI Tool",
            url: ":targetId",
            component: ShadowAIToolDetail,
          },
        },
      },
      models: {
        title: "Models",
        url: "models",
        component: ShadowAIRoot,
        indexComponent: ShadowAISectionRedirect,
        subPages: {
          detail: {
            title: "Shadow AI Tool",
            url: ":targetId",
            component: ShadowAIToolDetail,
          },
        },
      },
      mcps: {
        title: "MCPs",
        url: "mcps",
        component: ShadowAIRoot,
        // The MCPs index was the approvals queue; it lands on the MCP
        // approvals tab of Guardrails rather than the policies list.
        indexComponent: ShadowMCPIndexRedirect,
        subPages: {
          detail: {
            title: "Shadow MCP Server",
            url: ":serverSlug",
            component: ShadowMCPServerDetail,
          },
        },
      },
    },
  },
  // The Shadow MCP paths predate the section and are sitting in bookmarks and
  // tickets, so they redirect permanently rather than 404. The end-user
  // request URLs (/shadow-mcp/request, /risk-policy-bypass/request) are
  // top-level routes in App.tsx, are quoted verbatim in block messages
  // already in people's terminals, and are deliberately left alone.
  shadowMCP: {
    title: "Shadow MCP",
    url: "shadow-mcp",
    component: ShadowAIRoot,
    indexComponent: ShadowMCPIndexRedirect,
    subPages: {
      detail: {
        title: "Shadow MCP Server",
        url: ":serverSlug",
        component: ShadowMCPServerLegacyRedirect,
      },
    },
  },
  sdks: {
    title: "SDKs",
    url: "sdks",
    icon: "code",
    component: SDK,
  },
  deployments: {
    title: "Deployments",
    url: "deployments",
    icon: "history",
    component: DeploymentsRoot,
    // The listing lives on the MCP page as a tab now; this index only
    // forwards. The detail route below stays put — it is the URL the CLI
    // prints after a push.
    indexComponent: RedirectToDeployments,
    subPages: {
      deployment: {
        title: "Overview",
        url: ":deploymentId",
        component: Deployment,
      },
    },
  },
  plugins: {
    title: "Plugins",
    url: "plugins",
    icon: "puzzle",
    component: PluginsRoot,
    indexComponent: Plugins,
    subPages: {
      detail: {
        title: "Plugin",
        url: ":pluginId",
        // PluginDetail renders every section itself, picking the active one
        // from the path — the subpages exist to own the URLs (same shape as
        // mcp.details).
        component: PluginDetail,
        subPages: {
          overview: {
            title: "Plugin Overview",
            url: "overview",
          },
          // Keep existing server links working after merging servers into overview.
          servers: {
            title: "Plugin Overview",
            url: "servers",
          },
          skills: {
            title: "Plugin Skills",
            url: "skills",
          },
          assignments: {
            title: "Plugin Assignments",
            url: "assignments",
          },
          settings: {
            title: "Plugin Settings",
            url: "settings",
          },
        },
      },
    },
  },
  settings: {
    title: "Project settings",
    url: "settings",
    icon: "settings",
    component: Settings,
  },
  docs: {
    title: "Docs",
    url: "https://docs.getgram.ai",
    icon: "book-open",
    external: true,
  },
} satisfies Record<string, RouteEntry>;

type RouteStructure = typeof ROUTE_STRUCTURE;

/**
 * The point of all this type magic is to make it so you only have to define the routes once
 * and the `useRoutes` hook can add a lot of extra utilities without losing the type safety.
 */

// Transform the AppRouteBasic into an AppRoute, recursing on subPages if present
// so that subPages keeps its route-specific type
type TransformAppRoute<T extends AppRouteBasic> = T extends {
  subPages: AppRoutesBasic;
}
  ? Omit<AppRoute, "subPages"> & TransformRouteToGoTo<T["subPages"]>
  : AppRoute;

type TransformElem<T> = T extends AppRouteBasic
  ? TransformAppRoute<T>
  : T extends AppRouteBasic
    ? TransformRouteToGoTo<T>
    : T;

type TransformRouteToGoTo<T> = {
  [K in keyof T]: TransformElem<T[K]>;
};

type RoutesWithGoTo = TransformRouteToGoTo<RouteStructure>;

export const useRoutes = (overrides?: {
  orgSlug?: string;
  projectSlug?: string;
}): RoutesWithGoTo => {
  const location = useLocation();
  const slugs = useSlugs();
  const orgSlug = overrides?.orgSlug ?? slugs.orgSlug;
  const projectSlug = overrides?.projectSlug ?? slugs.projectSlug;
  const navigate = useNavigate();

  // Check if the current url matches the route url, including dynamic segments
  const matchesCurrent = (url: string) => {
    const urlParts = url.split("/").filter(Boolean);
    const currentParts = location.pathname.split("/").filter(Boolean);

    // Splat routes (trailing `*`, e.g. the costs drill) match any deeper path
    // that shares their prefix — keeps the sidebar item active while drilling.
    if (urlParts[urlParts.length - 1] === "*") {
      const prefix = urlParts.slice(0, -1);
      return (
        currentParts.length >= prefix.length &&
        prefix.every(
          (part, index) => part === currentParts[index] || part.startsWith(":"),
        )
      );
    }

    if (urlParts.length !== currentParts.length) {
      return false;
    }

    return urlParts.every(
      (part, index) => part === currentParts[index] || part.startsWith(":"),
    );
  };

  const addRouteUtilities = (
    route: AppRouteBasic,
    parent?: string,
    staticSiblings?: Set<string>,
  ): AppRoute => {
    if (parent === undefined && !route.url.startsWith("/")) {
      parent = `/:orgSlug/projects/:projectSlug`;
    }

    const urlWithParent = `${parent ?? ""}/${route.url}`;

    const resolveUrl = (...params: string[]) => {
      if (route.external) {
        return route.url;
      }

      const parts = urlWithParent.split("/").filter(Boolean);
      const finalParts = [];

      for (const part of parts) {
        if (part.startsWith(":")) {
          if (part === ":orgSlug") {
            finalParts.push(orgSlug);
          } else if (part === ":projectSlug") {
            finalParts.push(projectSlug);
          } else {
            const v = params.shift();
            if (!v) {
              // Instead of throwing an error, fallback to home page
              console.warn(
                `No value provided for ${part}, falling back to home page`,
              );
              return `/${orgSlug}/projects/${projectSlug}`;
            }
            finalParts.push(v);
          }
        } else {
          finalParts.push(part);
        }
      }

      return ("/" + finalParts.join("/")).replace(/\/+/g, "/");
    };

    const goTo = (...params: string[]) => {
      void navigate(resolveUrl(...params));
    };

    const linkComponent = ({
      params = [],
      queryParams = {},
      hash,
      className,
      children,
    }: {
      params?: string[];
      queryParams?: Record<string, string>;
      hash?: string;
      className?: string;
      children: React.ReactNode;
    }) => {
      const queryString = new URLSearchParams(queryParams).toString();
      const hashString = hash ? `#${hash}` : "";
      const queryPart = queryString ? `?${queryString}` : "";
      return (
        <Link
          to={`${resolveUrl(...params)}${queryPart}${hashString}`}
          className={cn("hover:underline", className)}
        >
          {children}
        </Link>
      );
    };

    const subPages = route.subPages
      ? addGoToToRoutes(route.subPages, urlWithParent)
      : undefined;

    // A dynamic route must not claim the current URL when a static sibling
    // owns it. `/mcp/:toolsetSlug` and `/mcp/add` have the same segment count
    // and matchesCurrent treats `:param` as a wildcard, so without this the
    // detail route reads as active on `/mcp/add` — which is what selects the
    // sidebar variant. The router already ranks static above dynamic; this
    // makes `active` agree with where the user actually landed.
    const shadowedByStaticSibling =
      route.url.startsWith(":") &&
      !!staticSiblings?.has(
        location.pathname.split("/").filter(Boolean)[
          (parent ?? "").split("/").filter(Boolean).length
        ] ?? "",
      );

    const active =
      (matchesCurrent(urlWithParent) && !shadowedByStaticSibling) ||
      !!Object.values(subPages ?? {}).some((subPage) => subPage.active);

    const newRoute: AppRoute = {
      ...route,
      active,
      Icon: route.customIcon
        ? route.customIcon
        : (props: Omit<IconProps, "name">) =>
            route.icon ? <Icon {...props} name={route.icon} /> : null,
      href: resolveUrl,
      goTo,
      Link: linkComponent,
      ...subPages,
    };

    return newRoute;
  };

  const addGoToToRoutes = <T extends AppRoutesBasic>(
    routes: T,
    parent?: string,
  ): TransformRouteToGoTo<T> => {
    const staticSiblings = new Set(
      Object.values(routes)
        .map((route) => route.url.split("/")[0] ?? "")
        .filter((segment) => segment !== "" && !segment.startsWith(":")),
    );
    return Object.fromEntries(
      Object.entries(routes).map(([key, route]) => [
        key,
        addRouteUtilities(route, parent, staticSiblings),
      ]),
    ) as TransformRouteToGoTo<T>;
  };

  const routes: RoutesWithGoTo = useMemo(
    () => addGoToToRoutes(ROUTE_STRUCTURE),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- addGoToToRoutes is stable in behavior; recompute only when pathname or slugs change
    [location.pathname, orgSlug, projectSlug, navigate],
  );

  return routes;
};

// --- Org-level routes ---

const ORG_ROUTE_STRUCTURE = {
  home: {
    title: "Home",
    url: "",
    icon: "house",
    component: OrgHome,
  },
  billing: {
    title: "Billing",
    url: "billing",
    icon: "credit-card",
    component: Billing,
  },
  team: {
    title: "Team",
    url: "team",
    icon: "users-round",
    component: Team,
  },
  apiKeys: {
    title: "API Keys",
    url: "api-keys",
    icon: "key-round",
    component: OrgApiKeys,
  },
  domains: {
    title: "Custom Domain",
    url: "domains",
    icon: "globe",
    component: OrgDomains,
  },
  logs: {
    title: "Logging & Telemetry",
    url: "logs",
    icon: "file-text",
    component: OrgLogs,
  },
  gateway: {
    title: "Gateway",
    url: "gateway",
    icon: "network",
    component: OrgGateway,
  },
  legacyData: {
    title: "Event Feed",
    url: "data",
    icon: "activity",
    component: LegacyDataRedirect,
  },
  data: {
    title: "Event Feed",
    url: "data/event-feed",
    icon: "activity",
    stage: "preview",
    component: EventFeed,
  },
  dataExports: {
    title: "Exports",
    url: "data/exports",
    icon: "send",
    component: DataExports,
  },
  skills: {
    title: "Skills",
    url: "skills",
    icon: "terminal",
    component: OrgSkills,
  },
  // Legacy URL: Platform MCP setup is what headless mode does now, so the old
  // standalone page redirects there. Kept out of the sidebar, and flagged so
  // the command palette does not offer it as a destination either.
  platformMcp: {
    title: "Platform MCP",
    url: "platform-mcp",
    legacyRedirect: true,
    component: PlatformMCPRedirect,
  },
  aiIntegrations: {
    title: "AI Integrations",
    url: "ai-integrations",
    icon: "bot",
    component: OrgAIIntegrations,
  },
  webhooks: {
    title: "Webhooks",
    url: "webhooks",
    icon: "webhook",
    stage: "beta",
    component: OrgWebhooks,
  },
  // Rolled out behind gram-user-session-issuer-dashboard, which the root
  // checks; the sidebar hides the entry while it is off.
  userSessionIssuers: {
    title: "Speakeasy Authorization Servers",
    url: "user-session-issuers",
    icon: "key-round",
    component: UserSessionIssuersRoot,
    indexComponent: UserSessionIssuersPage,
    subPages: {
      issuerDetail: {
        title: "Speakeasy Authorization Server",
        url: ":issuerId",
        component: UserSessionIssuerDetail,
      },
    },
  },
  externalServices: {
    title: "External Services",
    url: "external-services",
    icon: "cloud",
    component: ExternalServicesRoot,
    indexComponent: ExternalServicesPage,
    subPages: {
      // Credentials are namespaced under their own collection segment so that a
      // second kind of external-service resource (encryption keys) can sit
      // beside them rather than having to share this level.
      //
      // The provider segment is part of the resource's own path because the
      // detail page is per-provider: each provider has its own get/update
      // endpoints and its own fields, so a deep link has to carry which one it
      // is rather than relying on state handed over from the list.
      credentialDetail: {
        title: "External Credential",
        url: "credentials/:provider/:credentialId",
        component: ExternalCredentialDetail,
        subPages: {
          overview: { title: "Overview", url: "overview" },
          kmsKeys: { title: "KMS Keys", url: "kms-keys" },
          settings: { title: "Settings", url: "settings" },
        },
      },
    },
  },
  encryptionKeys: {
    title: "Encryption Keys",
    url: "encryption-keys",
    icon: "key-square",
    component: EncryptionKeysRoot,
    indexComponent: EncryptionKeysPage,
    subPages: {
      // Keyed on the provider for the same reason credentials are: the detail
      // page is per-provider, with its own get/update endpoints and its own
      // fields, so a deep link has to carry which provider it names rather than
      // relying on state handed over from the list.
      keyDetail: {
        title: "Encryption Key",
        url: ":provider/:keyId",
        component: ExternalKeyDetail,
        subPages: {
          overview: { title: "Overview", url: "overview" },
          signingKeys: { title: "Signing Keys", url: "signing-keys" },
          settings: { title: "Settings", url: "settings" },
        },
      },
    },
  },
  // Signing key sets are listed on the Encryption Keys page (a set is the
  // published face of the KMS key that backs it), but their detail pages get
  // their own top-level path so a set id can never be mistaken for the
  // :provider/:keyId segments of an encryption key. The index redirects to
  // that list, which keeps the breadcrumb and command palette entry landing
  // somewhere real. Not in the sidebar.
  signingKeySets: {
    title: "Signing Key Sets",
    url: "signing-keys",
    icon: "key-round",
    component: SigningKeySetsRoot,
    indexComponent: SigningKeySetsIndex,
    subPages: {
      setDetail: {
        title: "Signing Key Set",
        url: ":setId",
        component: JsonWebKeySetDetail,
        subPages: {
          overview: { title: "Overview", url: "overview" },
          keys: { title: "Keys", url: "keys" },
          settings: { title: "Settings", url: "settings" },
        },
      },
    },
  },
  // The trust policy is configured for the organization as a whole, so the
  // Access Hub names no project.
  workloadIssuers: {
    title: "Access Hub",
    url: "access-hub",
    icon: "cpu",
    stage: "preview",
    component: WorkloadIssuersRoot,
    indexComponent: WorkloadIssuersPage,
    subPages: {
      issuerDetail: {
        title: "Trusted Platform",
        url: ":issuerId",
        component: WorkloadIssuerDetailPage,
      },
      catalogPlatform: {
        title: "Catalog Platform",
        url: "catalog/:platformKey",
        component: CatalogPlatformPage,
      },
    },
  },
  auditLogs: {
    title: "Audit Logs",
    url: "audit-logs",
    icon: "history",
    component: OrgAuditLogs,
  },
  // Killswitches are managed on the identity of the person they restrict, so
  // this route no longer carries a roster: its index forwards to the people it
  // would have listed, and the detail record stays where the audit log links
  // to it.
  killswitch: {
    title: "Killswitch",
    url: "killswitch",
    icon: "shield-off",
    component: KillswitchesRoot,
    indexComponent: KillswitchIndexRedirect,
    subPages: {
      detail: {
        title: "Killswitch detail",
        url: ":killswitchId",
        component: KillswitchRecordRedirect,
      },
    },
  },
  legacyAgents: {
    title: "Agent Identity",
    url: "agent-management",
    legacyRedirect: true,
    component: ProjectIdentityRedirect,
  },
  legacyMcpSessions: {
    title: "MCP Sessions",
    url: "mcp-sessions",
    legacyRedirect: true,
    component: ProjectIdentityRedirect,
  },
  legacyRemoteIdentityProviders: {
    title: "Remote Identity Providers",
    url: "remote-identity-providers/*",
    legacyRedirect: true,
    component: ProjectIdentityRedirect,
  },
  identity: {
    title: "IDP and SSO",
    url: "identity",
    icon: "fingerprint",
    component: OrgIdentity,
  },
  // Platform Admin pages — the former floating Developer Toolkit, one page per
  // old tab. Speakeasy staff only (plus local dev); see PlatformAdminGate.
  platformAdminOverview: {
    title: "Platform Admin Overview",
    url: "platform-admin",
    icon: "crown",
    component: PlatformAdminOverview,
  },
  platformAdminRbac: {
    title: "RBAC Override",
    url: "platform-admin/rbac",
    icon: "shield",
    component: PlatformAdminRbacOverride,
  },
  platformAdminOnboarding: {
    title: "Enterprise Onboarding",
    url: "platform-admin/onboarding",
    icon: "mail",
    component: PlatformAdminOnboarding,
  },
  platformAdminOpenRouterKeys: {
    title: "OpenRouter Keys",
    url: "platform-admin/openrouter-keys",
    icon: "key-round",
    component: PlatformAdminOpenRouterKeys,
  },
  deviceAgent: {
    title: "Device Agent",
    url: "device-agent",
    icon: "laptop",
    component: DeviceAgentRoot,
    indexComponent: DeviceAgent,
    subPages: {
      configuration: {
        title: "Configuration",
        url: "configuration",
        component: DeviceAgent,
      },
      scanTargets: {
        title: "Scan Targets",
        url: "scan-targets",
        component: DeviceAgent,
      },
      mdmIntegrations: {
        title: "MDM Integrations",
        url: "mdm-integrations",
        component: DeviceAgent,
      },
      mdmDetail: {
        title: "MDM Integration",
        url: "mdm-integrations/:provider",
        component: MdmIntegrationDetail,
      },
    },
  },
  access: {
    title: "Roles & Permissions",
    url: "access",
    icon: "shield",
    component: Access,
    subPages: {
      roles: {
        title: "Roles & Permissions",
        url: "roles",
        component: Access,
      },
      members: {
        title: "Roles & Permissions",
        url: "members",
        component: Access,
      },
      challenges: {
        title: "Roles & Permissions",
        url: "challenges",
        component: Access,
      },
    },
  },
  // Role authoring is its own page, not a sheet: a role can carry a dozen
  // permissions, each with rules of its own, which is more than a sheet can
  // show without scrolling away the thing being edited. These are siblings of
  // `access` rather than sub-pages because the access page renders tabs, not
  // an outlet.
  createRole: {
    title: "Create Role",
    url: "access/roles/create",
    component: RoleEditorPage,
  },
  editRole: {
    title: "Edit Role",
    url: "access/roles/:roleId/edit",
    component: RoleEditorPage,
  },
  requestAccess: {
    title: "Request Access",
    url: "request-access",
    component: RequestAccess,
    outsideMainLayout: true,
  },
  setup: {
    title: "Setup",
    url: "setup",
    icon: "settings",
    component: SetupWizard,
    outsideMainLayout: true,
  },
  // Legacy per-card pages (setup/idp, setup/wizard, ...) open the wizard on
  // that card.
  setupTask: {
    title: "Setup task",
    url: "setup/:taskSlug",
    icon: "list-checks",
    component: SetupTaskRedirect,
    outsideMainLayout: true,
  },
  // Headless mode renders its own chrome (mode tabs only, no sidebar or
  // workspace header), so it sits outside OrgLayout.
  headless: {
    title: "Headless",
    url: "headless",
    component: HeadlessMode,
    outsideMainLayout: true,
  },
} satisfies Record<string, RouteEntry>;

type OrgRouteStructure = typeof ORG_ROUTE_STRUCTURE;
type OrgRoutesWithGoTo = TransformRouteToGoTo<OrgRouteStructure>;

function routePaths(
  routes: Record<string, RouteEntry>,
  parent?: string,
): string[] {
  return Object.values(routes).flatMap((route) => {
    if (!route.url) return [];
    const path = parent ? `${parent}/${route.url}` : route.url;
    return [path, ...routePaths(route.subPages ?? {}, path)];
  });
}

/**
 * The org-relative path of every org-level route, nested pages included (for
 * redirect logic). A detail page such as "access-hub/:issuerId" has to be
 * listed with its parent, or its URL reads as a path inside a project that
 * happens to share the parent's slug.
 */
export const orgRoutePaths = routePaths(ORG_ROUTE_STRUCTURE);

export const useOrgRoutes = (): OrgRoutesWithGoTo => {
  const location = useLocation();
  const { orgSlug } = useSlugs();
  const navigate = useNavigate();

  const matchesCurrent = (url: string) => {
    const urlParts = url.split("/").filter(Boolean);
    const currentParts = location.pathname.split("/").filter(Boolean);

    if (urlParts.length !== currentParts.length) {
      return false;
    }

    return urlParts.every(
      (part, index) => part === currentParts[index] || part.startsWith(":"),
    );
  };

  const addRouteUtilities = (
    route: AppRouteBasic,
    parent?: string,
  ): AppRoute => {
    if (parent === undefined && !route.url.startsWith("/")) {
      parent = `/:orgSlug`;
    }

    const urlWithParent = `${parent ?? ""}/${route.url}`;

    const resolveUrl = (...params: string[]) => {
      if (route.external) {
        return route.url;
      }

      const parts = urlWithParent.split("/").filter(Boolean);
      const finalParts = [];

      for (const part of parts) {
        if (part.startsWith(":")) {
          if (part === ":orgSlug") {
            finalParts.push(orgSlug);
          } else {
            const v = params.shift();
            if (!v) {
              console.warn(
                `No value provided for ${part}, falling back to org home`,
              );
              return `/${orgSlug}`;
            }
            finalParts.push(v);
          }
        } else {
          finalParts.push(part);
        }
      }

      return ("/" + finalParts.join("/")).replace(/\/+/g, "/");
    };

    const goTo = (...params: string[]) => {
      void navigate(resolveUrl(...params));
    };

    const linkComponent = ({
      params = [],
      queryParams = {},
      hash,
      className,
      children,
    }: {
      params?: string[];
      queryParams?: Record<string, string>;
      hash?: string;
      className?: string;
      children: React.ReactNode;
    }) => {
      const queryString = new URLSearchParams(queryParams).toString();
      const hashString = hash ? `#${hash}` : "";
      const queryPart = queryString ? `?${queryString}` : "";
      return (
        <Link
          to={`${resolveUrl(...params)}${queryPart}${hashString}`}
          className={cn("hover:underline", className)}
        >
          {children}
        </Link>
      );
    };

    const subPages = route.subPages
      ? addGoToToRoutes(route.subPages, urlWithParent)
      : undefined;

    const active =
      matchesCurrent(urlWithParent) ||
      !!Object.values(subPages ?? {}).some((subPage) => subPage.active);

    const newRoute: AppRoute = {
      ...route,
      active,
      Icon: route.customIcon
        ? route.customIcon
        : (props: Omit<IconProps, "name">) =>
            route.icon ? <Icon {...props} name={route.icon} /> : null,
      href: resolveUrl,
      goTo,
      Link: linkComponent,
      ...subPages,
    };

    return newRoute;
  };

  const addGoToToRoutes = <T extends AppRoutesBasic>(
    routes: T,
    parent?: string,
  ): TransformRouteToGoTo<T> => {
    return Object.fromEntries(
      Object.entries(routes).map(([key, route]) => [
        key,
        addRouteUtilities(route, parent),
      ]),
    ) as TransformRouteToGoTo<T>;
  };

  const routes: OrgRoutesWithGoTo = useMemo(
    () => addGoToToRoutes(ORG_ROUTE_STRUCTURE),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- addGoToToRoutes is stable in behavior; recompute only when pathname or slug change
    [location.pathname, orgSlug, navigate],
  );

  return routes;
};
